import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { buildDealCommission, type DealCommissionPayload } from "@/lib/deal-commission";

export type DealCommissionFieldState = {
  amount: string;
  memberId: string;
  memberLabel: string;
  freeName: string;
  useTypedName: boolean;
};

export const emptyDealCommission: DealCommissionFieldState = {
  amount: "",
  memberId: "",
  memberLabel: "",
  freeName: "",
  useTypedName: false,
};

type MemberOption = { id: string; display_name: string | null };

type DealCommissionFieldsProps = {
  members: MemberOption[];
  value: DealCommissionFieldState;
  onChange: (next: DealCommissionFieldState) => void;
};

const OTHER = "__other__";
const NONE = "__none__";

export function commissionFromDeal(deal: {
  commission_amount?: number | null;
  commission_member_id?: string | null;
  commission_name?: string | null;
}): DealCommissionFieldState {
  const memberId = deal.commission_member_id ?? "";
  const name = deal.commission_name ?? "";
  return {
    amount: deal.commission_amount != null && deal.commission_amount > 0 ? String(deal.commission_amount) : "",
    memberId,
    memberLabel: memberId ? name : "",
    freeName: memberId ? "" : name,
    useTypedName: !memberId && Boolean(name),
  };
}

export function toCommissionPayload(value: DealCommissionFieldState): DealCommissionPayload {
  return buildDealCommission({
    amountRaw: value.amount,
    memberId: value.memberId,
    memberLabel: value.memberLabel,
    freeName: value.freeName,
  });
}

export function DealCommissionFields({ members, value, onChange }: DealCommissionFieldsProps) {
  const recipientValue = value.memberId ? value.memberId : value.useTypedName ? OTHER : NONE;

  return (
    <div className="grid grid-cols-2 gap-3">
      <div className="space-y-2">
        <Label htmlFor="deal-commission-amount">Comissão (R$)</Label>
        <Input
          id="deal-commission-amount"
          type="number"
          min={0}
          step={0.01}
          value={value.amount}
          onChange={(e) => onChange({ ...value, amount: e.target.value })}
          placeholder="0,00"
        />
      </div>
      <div className="space-y-2">
        <Label>Quem recebe</Label>
        <Select
          value={recipientValue}
          onValueChange={(v) => {
            if (v === NONE) {
              onChange({ ...value, memberId: "", memberLabel: "", freeName: "", useTypedName: false });
              return;
            }
            if (v === OTHER) {
              onChange({ ...value, memberId: "", memberLabel: "", useTypedName: true });
              return;
            }
            const member = members.find((m) => m.id === v);
            onChange({
              ...value,
              memberId: v,
              memberLabel: member?.display_name || "Membro",
              freeName: "",
              useTypedName: false,
            });
          }}
        >
          <SelectTrigger>
            <SelectValue placeholder="Membro ou outro nome" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={NONE}>Ninguém</SelectItem>
            {members.map((m) => (
              <SelectItem key={m.id} value={m.id}>
                {m.display_name || "Membro"}
              </SelectItem>
            ))}
            <SelectItem value={OTHER}>Outra pessoa (digitar nome)</SelectItem>
          </SelectContent>
        </Select>
      </div>
      {recipientValue === OTHER && (
        <div className="col-span-2 space-y-2">
          <Label htmlFor="deal-commission-name">Nome de quem recebe</Label>
          <Input
            id="deal-commission-name"
            value={value.freeName}
            onChange={(e) => onChange({ ...value, freeName: e.target.value, memberId: "", memberLabel: "", useTypedName: true })}
            placeholder="Digite o nome"
          />
        </div>
      )}
    </div>
  );
}
